/*
We're constantly improving the code you see. 
Please share your feedback here: https://form.asana.com/?k=uvp-HPgd3_hyoXRBw1IcNg&d=1152665201300829
*/

import React, { useRef, useEffect, useState } from "react";
import { Platform, View, TextInput, StyleSheet } from 'react-native';
import { Image, FileText, Paperclip, X } from 'lucide-react-native';
import { CmpAttachbutton } from "../../icons/CmpAttachbutton";
import { CmpSendbutton } from "../CmpSendbutton";
import "./style.css";

interface CmpComposerProps {
  className?: string;
  value?: string;
  onChange?: (value: string) => void;
  onSend?: () => void;
  onPaperclip?: () => void;
  onHistoryClick?: () => void;
  sendButtonState?: 'default' | 'disabled' | 'sending';
  attachButtonState?: 'default' | 'disabled';
  onSendClick?: () => void;
  attachments?: any[];
  onRemoveAttachment?: (index: number) => void;
  textareaRef?: any;
  style?: any;
  [key: string]: any;
}

export const CmpComposer: React.FC<CmpComposerProps> = ({
  className = '',
  value = '',
  onChange = () => {},
  onSend = () => {},
  onPaperclip = () => {},
  onHistoryClick = () => {}, // kept for backward compatibility; history button is no longer shown
  sendButtonState = 'default',
  attachButtonState = 'default',
  onSendClick = () => {},
  attachments = [],
  onRemoveAttachment = () => {},
  textareaRef,
  style = {},
  ...props
}) => {
  const internalTextareaRef = useRef<any>(null);
  const effectiveRef = textareaRef || internalTextareaRef;
  const [inputHeight, setInputHeight] = useState(40);

  const handleKeyDown = (e: any) => {
    if (Platform.OS === 'web') {
      // Mission 2: Enter/Return should create a new line (like iMessage).
      // Use Cmd/Ctrl+Enter as a convenience to send.
      if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        onSend();
      }
    }
  };

  const handleTextareaChange = (text: string) => {
    onChange(text);
    
    // Auto-resize for web
    if (Platform.OS === 'web' && effectiveRef.current) {
      const textarea = effectiveRef.current;
      textarea.style.height = 'auto';
      textarea.style.height = `${textarea.scrollHeight}px`;
    }
  };

  const handleNativeChange = (text: string) => {
    onChange(text);
  };

  const handleContentSizeChange = (event: any) => {
    // Auto-resize for React Native
    if (Platform.OS !== 'web') {
      setInputHeight(Math.max(40, event.nativeEvent.contentSize.height));
    }
  };

  // Reset height when value is cleared
  useEffect(() => {
    if (!value && effectiveRef.current) {
      if (Platform.OS === 'web') {
        effectiveRef.current.style.height = 'auto';
        const baseHeight = effectiveRef.current.scrollHeight;
        effectiveRef.current.style.height = `${baseHeight}px`;
      } else {
        setInputHeight(40);
      }
    }
  }, [value]);

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  };

  const getFileIcon = (type: string) => {
    switch (type) {
      case 'image':
        return <Image size={12} />;
      case 'document':
        return <FileText size={12} />;
      default:
        return <Paperclip size={12} />;
    }
  };

  // Web rendering
  if (Platform.OS === 'web') {
    return (
      <div className={`cmp-composer ${className}`} style={style} {...props}>
        <CmpAttachbutton 
          className="cmp-attachbutton" 
          state={attachButtonState}
          onClick={onPaperclip} 
        />
        
        <div className="composer-input-container">
          {/* Deprecated legacy pills hidden; modern thumbnail grid is rendered by Chat.tsx */}
          <textarea
            ref={effectiveRef}
            className="composer-input"
            placeholder="Chat with Cedar..."
            value={value}
            onChange={(e) => handleTextareaChange(e.target.value)}
            onKeyDown={handleKeyDown}
            autoComplete="off"
            rows={1}
            style={{ resize: 'none' }}
          />
        </div>
        
        <CmpSendbutton 
          className="cmp-sendbutton-instance" 
          state={sendButtonState}
          onClick={onSendClick}
        />
      </div>
    );
  }

  // Native rendering
  return (
    <View style={[nativeStyles.container, style]}>
      {/* Attach button */}
      <CmpAttachbutton 
        state={attachButtonState}
        onClick={onPaperclip} 
      />
      
      {/* Input container */}
      <View style={nativeStyles.inputContainer}>
        <TextInput
          ref={effectiveRef}
          style={[nativeStyles.input, { height: inputHeight }]}
          placeholder="Chat with Cedar..."
          placeholderTextColor="#999"
          value={value}
          onChangeText={handleNativeChange}
          multiline
          onContentSizeChange={handleContentSizeChange}
          returnKeyType="default"
          blurOnSubmit={false}
        />
      </View>
      
      {/* Send button */}
      <CmpSendbutton 
        state={sendButtonState}
        className=""
        onClick={onSendClick}
      />
    </View>
  );
};

// Native styles (used only on iOS/Android)
const nativeStyles = StyleSheet.create({
  container: {
    flexDirection: 'row',
    alignItems: 'flex-end',
    paddingHorizontal: 12,
    paddingVertical: 8,
    backgroundColor: '#FFFFFF',
    borderTopWidth: 1,
    borderTopColor: '#E5E7EB',
  },
  inputContainer: {
    flex: 1,
    marginHorizontal: 8,
    backgroundColor: '#F9FAFB',
    borderRadius: 20,
    paddingHorizontal: 12,
    paddingVertical: 8,
  },
  input: {
    minHeight: 40,
    maxHeight: 120,
    fontSize: 16,
    color: '#1F2937',
    lineHeight: 20,
  },
});

